import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import DiscountCodeBox from "@/components/DiscountCodeBox";
import { Check, Loader2, ExternalLink, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Prijzen-pagina: 2 kaarten (Standaard / Premium, incl. BTW). Prijzen komen van GET /api/pricing:
// reguliere prijs (verlenging) + prijs eerste jaar (introductieprijs zolang die in /beheer aan staat).
// User kiest betaalmethode (iDEAL / creditcard) en start de Mollie-checkout.
// Bij succes redirect Mollie de user terug naar /payment-status?payment_id=...
// Geen "Meest gekozen"-badge (op verzoek user 2026-03-05).

const fmtPrice = (v) => (v == null ? "—" : `€${String(v).replace(/\.00$/, "").replace(".", ",")}`);
const fmtDate = (iso) => new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });

const PLANS = [
  {
    key: "STANDARD",
    name: "Standaard",
    price: "129",
    tagline: "Voor de meeste sportclubs — alles wat je nodig hebt om vooruit te plannen.",
    features: [
      "Tot 3 clubs onder één account",
      "Onbeperkt scenario's per club",
      "Historische cijfers + afschrijvingsstaat + subposten",
      "AI PDF-import voor jaarverslagen (Gemini)",
      "Reserves + investeringsplannen + rolling scenarios",
      "Excel / CSV / PDF export met eigen huisstijl",
      "Wachtwoord vergeten + invite-only teamtoegang",
    ],
    not_included: [
      "NPV-modules (IRR, terugverdientijd, NPV/€)",
      "Monte Carlo (tornado + fan chart)",
      "Multi-user samenwerking",
    ],
  },
  {
    key: "PREMIUM",
    name: "Premium",
    price: "159",
    tagline: "Voor clubs die risico's willen modelleren en samen willen werken aan het plan.",
    features: [
      "Tot 5 clubs onder één account",
      "Alles uit Standaard",
      "NPV-kerncijfers: IRR, NPV/€, gedisconteerde terugverdientijd",
      "Monte Carlo-simulatie (tornado + fan chart + XLSX/PDF export)",
      "Multi-user per club (tot 4 medewerkers, viewer/editor)",
      "Clubdocumenten: Clubmap + Inbox op de server, importbestanden automatisch gearchiveerd",
      "Chart-toelichtingen en scenario-samenvatting op PDF",
    ],
    not_included: [],
  },
];

const METHODS = [
  { id: "ideal", label: "iDEAL", hint: "Direct betalen · legt SEPA-mandaat vast" },
  { id: "creditcard", label: "Creditcard", hint: "Visa / Mastercard · kaart-mandaat" },
];

function PlanCard({ plan, pricing, method, onMethodChange, onBuy, busyKey, isCurrent, isDowngrade, discountPrice }) {
  const t = useT();
  const busy = busyKey === plan.key;
  const disabled = busy || isCurrent;
  const key = plan.key.toLowerCase();
  const regular = pricing?.regular ?? plan.price;
  const firstYear = pricing?.first_year ?? plan.price;
  const intro = !!pricing?.intro_active;
  const shown = discountPrice || firstYear;
  const struck = discountPrice ? firstYear : intro ? regular : null;
  return (
    <div
      data-testid={`plan-card-${key}`}
      className="relative flex flex-col gap-4 border-2 border-foreground bg-card heavy-shadow p-6 md:p-8"
    >
      {intro && (
        <div data-testid={`plan-intro-badge-${key}`} className="absolute -top-3 left-6 bg-accent text-accent-foreground border-2 border-foreground px-2.5 py-0.5 text-[11px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> {t("Introductieprijs")}
        </div>
      )}
      <div>
        <div className="stat-label" data-testid={`plan-name-${key}`}>{plan.name}</div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className={`font-display font-black text-5xl ${discountPrice ? "text-positive" : ""}`} data-testid={`plan-price-${key}`}>{fmtPrice(shown)}</span>
          {struck && (
            <span className="font-display font-bold text-2xl text-muted-foreground line-through" data-testid={`plan-price-original-${key}`}>{fmtPrice(struck)}</span>
          )}
          <span className="text-sm text-muted-foreground">/{t("jaar")}</span>
        </div>
        {intro ? (
          <div className="text-xs mt-1" data-testid={`plan-intro-note-${key}`}>
            <span className="font-semibold">{t("Geldig voor het eerste jaar")}</span>
            <span className="text-muted-foreground"> · {t("daarna")} {fmtPrice(regular)}/{t("jaar")} · {t("incl. 21% BTW · automatische jaarlijkse verlenging")}</span>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground mt-1">{t("Prijs incl. 21% BTW · automatische jaarlijkse verlenging")}</div>
        )}
        <p className="text-sm mt-3 leading-relaxed">{plan.tagline}</p>
      </div>

      <ul className="space-y-2">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 mt-0.5 shrink-0 text-positive" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {plan.not_included.length > 0 && (
        <div className="pt-2 mt-1 border-t border-border">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">{t("Niet inbegrepen")}</div>
          <ul className="space-y-1">
            {plan.not_included.map((f, i) => (
              <li key={i} className="text-xs text-muted-foreground line-through">{f}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto space-y-3">
        <div>
          <div className="stat-label mb-2">{t("Betaalmethode")}</div>
          <div className="flex flex-col gap-1.5">
            {METHODS.map((m) => (
              <label
                key={m.id}
                data-testid={`plan-method-${plan.key.toLowerCase()}-${m.id}`}
                className={`flex items-center gap-2 border-2 px-3 py-2 cursor-pointer ${
                  method === m.id ? "border-foreground bg-primary/5" : "border-border hover:border-foreground/40"
                }`}
              >
                <input
                  type="radio"
                  name={`method-${plan.key}`}
                  value={m.id}
                  checked={method === m.id}
                  onChange={() => onMethodChange(m.id)}
                  className="accent-foreground"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{m.label}</div>
                  <div className="text-[11px] text-muted-foreground">{m.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {isCurrent ? (
          <button
            type="button"
            disabled
            data-testid={`plan-cta-current-${plan.key.toLowerCase()}`}
            className="btn-brutal bg-muted text-muted-foreground w-full px-5 py-3 font-display uppercase font-black text-sm cursor-not-allowed"
          >
            {t("Huidig abonnement")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onBuy(plan.key, method)}
            disabled={disabled}
            data-testid={`plan-cta-${plan.key.toLowerCase()}`}
            className="btn-brutal bg-primary text-primary-foreground w-full px-5 py-3 font-display uppercase font-black text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {t("Doorverwijzen…")}</>
            ) : (
              <>{isDowngrade ? t("Wissel naar {plan}").replace("{plan}", plan.name) : t("Bestel nu")} <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default function PricingPage() {
  const t = useT();
  const nav = useNavigate();
  const { user } = useAuth();
  const [method, setMethod] = useState({ STANDARD: "ideal", PREMIUM: "ideal" });
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => setPricing(null));
  }, []);

  const currentTier = (user?.tier || "").toUpperCase();
  const introActive = !!pricing?.intro?.active;

  const handleBuy = async (planKey, methodId) => {
    if (!user) { nav("/login?next=/prijzen"); return; }
    setBusyKey(planKey);
    setError(null);
    try {
      const { data } = await api.post("/mollie/checkout", {
        plan: planKey, method: methodId, discount_code: discount?.code || null,
      });
      if (data?.checkout_url) {
        // Bewaar payment_id in sessionStorage zodat de statuspagina 'm terug
        // kan vinden ook als Mollie 'm niet als querystring meegeeft.
        if (data.payment_id) {
          try { sessionStorage.setItem("mollie_last_payment_id", data.payment_id); } catch { /* private mode */ }
        }
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error(t("Onverwacht antwoord van de betaalprovider."));
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || t("Er ging iets mis.");
      setError(msg);
      toast.error(msg);
      setBusyKey(null);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="pricing-page">
        {/* Header */}
        <div className="mb-8">
          <div className="stat-label">{t("Abonnementen")}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase mt-1 leading-none">
            {t("Kies je plan")}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-2xl">
            {t("Alle plannen worden éénmalig per jaar afgeschreven via SEPA-incasso of creditcard. Opzeggen kan op elk moment vóór de volgende incassodatum.")}
          </p>
          {introActive && (
            <div className="mt-4 inline-flex items-start gap-2 border-2 border-foreground bg-accent/30 px-3 py-2 text-sm max-w-2xl" data-testid="pricing-intro-banner">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                <b>{t("Introductieprijs")}:</b> {t("je betaalt het eerste jaar")} {fmtPrice(pricing.plans.STANDARD.first_year)} (Standaard) {t("of")} {fmtPrice(pricing.plans.PREMIUM.first_year)} (Premium).
                {" "}{t("Vanaf het tweede jaar geldt de reguliere prijs van")} {fmtPrice(pricing.plans.STANDARD.regular)} {t("resp.")} {fmtPrice(pricing.plans.PREMIUM.regular)} {t("per jaar")}.
                {pricing.intro.ends_at && <> {t("De actie geldt voor bestellingen t/m")} {fmtDate(pricing.intro.ends_at)}.</>}
              </span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2" data-testid="pricing-terms-note">
            {t("Door te bestellen ga je akkoord met de")}{" "}
            <Link to="/voorwaarden" className="underline hover:text-foreground">{t("algemene voorwaarden")}</Link>.
          </p>
        </div>

        {error && (
          <div
            data-testid="pricing-error"
            className="mb-6 border-2 border-destructive bg-destructive/10 text-destructive p-4 text-sm"
          >
            {error}
          </div>
        )}

        {/* Kortingscode */}
        <div className="mb-6 max-w-xl">
          <DiscountCodeBox discount={discount} onApplied={setDiscount} onClear={() => setDiscount(null)} />
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6">
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              pricing={pricing?.plans?.[plan.key]}
              method={method[plan.key]}
              onMethodChange={(m) => setMethod((prev) => ({ ...prev, [plan.key]: m }))}
              onBuy={handleBuy}
              busyKey={busyKey}
              isCurrent={currentTier === plan.key}
              isDowngrade={currentTier === "PREMIUM" && plan.key === "STANDARD"}
              discountPrice={discount?.prices?.[plan.key] || null}
            />
          ))}
        </div>

        {/* Trust strip */}
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3 border border-border p-4 bg-card">
            <ShieldCheck className="w-5 h-5 mt-0.5 text-primary shrink-0" />
            <div>
              <div className="font-semibold text-sm">{t("Veilig via Mollie")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t("Nederlandse betaaldienst · PSD2 & PCI-DSS gecertificeerd")}</div>
            </div>
          </div>
          <div className="flex items-start gap-3 border border-border p-4 bg-card">
            <Check className="w-5 h-5 mt-0.5 text-positive shrink-0" />
            <div>
              <div className="font-semibold text-sm">{t("Geen setup-kosten")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t("Je betaalt alleen het jaarabonnement — inclusief support")}</div>
            </div>
          </div>
          <div className="flex items-start gap-3 border border-border p-4 bg-card">
            <ExternalLink className="w-5 h-5 mt-0.5 text-primary shrink-0" />
            <div>
              <div className="font-semibold text-sm">{t("Vragen over prijzen?")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("Mail")} <a className="underline" href="mailto:info@club-fin.nl">info@club-fin.nl</a>
              </div>
            </div>
          </div>
        </div>

        {/* Extra info block */}
        <div className="mt-8 text-xs text-muted-foreground max-w-2xl leading-relaxed">
          {t("Meer clubs onder één account nodig? Neem contact op via")}{" "}
          <a className="underline" href="mailto:info@club-fin.nl">info@club-fin.nl</a>{" "}
          {t("voor een offerte op maat.")}
        </div>

        <div className="mt-6">
          <Link
            to="/"
            data-testid="pricing-back-to-dashboard"
            className="text-sm text-muted-foreground hover:text-foreground underline"
          >
            ← {t("Terug naar dashboard")}
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
